/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { DeviceType, globalRef } from '@amazon/vinyl-util'
import { type Auth, AuthStatus } from '@/auth/auth'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import { LwaAuth } from '@/auth/LwaAuth'
import { getDeviceConfig } from '@/device/deviceConfig'
import { CblAuth } from '@/auth/CblAuth'
import { getCodeData } from '@/auth/createCodeData'
import { localSignInPersistence } from '@/auth/signInPersistence'
import { displayPublicCodePrompt } from '@/auth/PublicCodePrompt'
import { MapAuth } from '@/auth/MapAuth'
import { type ObservableValue } from '@amazon/vinyl-observable'

export const authRef = globalRef<Auth>(() => {
    const config = getDeviceConfig()
    if (config.usesMapAuth) {
        return new MapAuth()
    } else if (config.deviceType === DeviceType.SMART_TV) {
        return new CblAuth({
            codeData: getCodeData(),
            persistence: localSignInPersistence,
            displayCodeCallback: displayPublicCodePrompt,
        })
    } else {
        return new LwaAuth()
    }
})

export function getAuth(): Auth {
    return authRef.value
}

export function getTokenProvider(): AuthTokenProvider {
    return authRef.value.authTokenProvider
}

export function getAuthToken(): Promise<string> {
    return authRef.value.authTokenProvider()
}

/**
 * A data provider for the current sign-in state.
 */
export const signedIn: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v === AuthStatus.SIGNED_IN)

export const notSignedIn: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v !== AuthStatus.SIGNED_IN)

let sessionId: string | null = null

/**
 * Creates or gets a unique id for this session.
 *
 * Must satisfy ^\d{3}-\d{7}-\d{7}$",
 */
export function getAnonSessionId(): string {
    if (!sessionId) {
        const part1 = Math.floor(Math.random() * 1e3)
            .toString()
            .padStart(3, '0')
        const part2 = Math.floor(Math.random() * 1e7)
            .toString()
            .padStart(7, '0')
        const part3 = Math.floor(Math.random() * 1e7)
            .toString()
            .padStart(7, '0')
        sessionId = `${part1}-${part2}-${part3}`
    }
    return sessionId
}
